import { useEffect } from 'react';
import axios from 'axios';
import { useWidgetDataAttr } from '@/hooks';
import { useSenderStore } from '@/hooks/useSenderStore';
import { WidgetLocation } from '@/types/widget';
import { sentryCaptureException } from './sentry';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const LOCATION_TO_API_URL = {
  [WidgetLocation.EAST_ASIA]:
    'https://sleekflow-core-app-eas-production.azurewebsites.net',
  [WidgetLocation.EAST_US]:
    'https://sleekflow-core-app-eus-production.azurewebsites.net',
  [WidgetLocation.SOUTHEAST_ASIA]:
    'https://sleekflow-core-app-seas-production.azurewebsites.net',
  [WidgetLocation.UAE_NORTH]:
    'https://sleekflow-core-app-uaen-production.azurewebsites.net',
  [WidgetLocation.WEST_EUROPE]:
    'https://sleekflow-core-app-weu-production.azurewebsites.net',
};

export const getApiUrlByLocation = (location: WidgetLocation | null) => {
  if (import.meta.env.VITE_ENV !== 'production' || !location) {
    return API_BASE_URL;
  }

  return LOCATION_TO_API_URL[location];
};

// Create axios instance with default configuration
export const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosClient.interceptors.request.use(
  (config) => {
    // Get signature from store and add to headers if available
    const signature = useSenderStore.getState().signature;
    if (signature) {
      config.headers['X-Sleekflow-LiveChat-Signature'] = signature;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    sentryCaptureException(error);
    return Promise.reject(error);
  },
);

export const useApiClientInit = () => {
  const { location } = useWidgetDataAttr();

  useEffect(() => {
    axiosClient.defaults.headers.common['X-Sleekflow-Location'] = location;
  }, [location]);
};
